import React, { useState, useEffect } from "react";
import { Tabs, Alert } from "antd";
import SegmentSelector from "./SegmentSelector";

const { TabPane } = Tabs;

const AudienceSection = ({
  openDropdown,
  toggleDropdown,
  filteredUsers,
  selectedUsers,
  handleUserSelection,
  handleSelectAll,
  selectAll,
  statusFilter,
  setStatusFilter,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  startTime,
  setStartTime,
  endTime,
  setEndTime,
  showFilters,
  toggleFilterPanel,
  errors,
  formData,
  handleChange,
  searchQuery,
  setSearchQuery,
  // NEW PROPS
  selectedSegments,
  setSelectedSegments,
  segmentUsers,
  setSegmentUsers,
  tenantEmail,
}) => {
  const [activeTab, setActiveTab] = useState("users");

  // Calculate total recipients
  const manualContactsCount = formData.contacts
    ? formData.contacts.split(",").filter((c) => c.trim()).length
    : 0;

  const totalRecipients =
    selectedUsers.length + manualContactsCount + segmentUsers.length;

  return (
    <div className="card mb-4 border-0 shadow-sm">
      <div
        className="card-header bg-white d-flex justify-content-between align-items-center py-3"
        onClick={toggleDropdown}
        style={{ cursor: "pointer" }}
      >
        <div className="d-flex align-items-center">
          <div
            className="step-number bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
            style={{
              width: "32px",
              height: "32px",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            1
          </div>
          <div>
            <h5 className="mb-0">Choose your Audience</h5>
            <small className="text-muted">
              {totalRecipients > 0
                ? `${totalRecipients} recipients selected`
                : "Select who will receive your campaign"}
              {selectedSegments.length > 0 && (
                <span className="text-primary ms-2">
                  ({selectedSegments.length} segments)
                </span>
              )}
            </small>
          </div>
        </div>
        <span className="text-muted fs-5">{openDropdown ? "▲" : "▼"}</span>
      </div>

      {openDropdown && (
        <div className="card-body pt-3">
          <div className="mb-4">
            <label className="form-label fw-semibold">
              Who will receive this message
            </label>

            {/* Manual Contacts Input (Always visible) */}
            <div className="mb-4">
              <label className="form-label small fw-semibold">
                Manual Contacts (Optional)
              </label>
              <textarea
                name="contacts"
                className={`form-control ${
                  errors.contacts ? "is-invalid" : ""
                }`}
                placeholder="Enter phone numbers separated by commas (e.g., +1234567890, +1987654321)"
                value={formData.contacts}
                onChange={handleChange}
                rows="2"
              />
              {errors.contacts && (
                <div className="invalid-feedback">{errors.contacts}</div>
              )}
              <div className="form-text">
                Enter international format numbers with country code
              </div>
            </div>

            {/* Tab Selection */}
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              className="mb-4"
            >
              <TabPane tab="Select Users" key="users">
                <div className="pt-3">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Select from your users</h6>
                    <button
                      className="btn btn-outline-primary btn-sm"
                      type="button"
                      onClick={toggleFilterPanel}
                    >
                      <i className="bi bi-funnel me-1"></i>{" "}
                      {showFilters ? "Hide" : "Show"} filters
                    </button>
                  </div>

                  {showFilters && (
                    <div className="card p-3 mb-4 border">
                      <h6 className="fw-semibold mb-3">Filter Users</h6>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <label className="form-label">Search</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search by name, phone, email or tag"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Status</label>
                          <select
                            className="form-select"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                          >
                            <option value="">All Statuses</option>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                          </select>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Date Range</label>
                          <div className="input-group">
                            <input
                              type="date"
                              value={startDate}
                              onChange={(e) => setStartDate(e.target.value)}
                              className="form-control"
                              placeholder="Start date"
                            />
                            <span className="input-group-text">to</span>
                            <input
                              type="date"
                              value={endDate}
                              onChange={(e) => setEndDate(e.target.value)}
                              className="form-control"
                              placeholder="End date"
                            />
                          </div>
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Time Range</label>
                          <div className="input-group">
                            <input
                              type="time"
                              value={startTime}
                              onChange={(e) => setStartTime(e.target.value)}
                              className="form-control"
                            />
                            <span className="input-group-text">to</span>
                            <input
                              type="time"
                              value={endTime}
                              onChange={(e) => setEndTime(e.target.value)}
                              className="form-control"
                            />
                          </div>
                        </div>

                        <div className="col-12 text-end">
                          <button
                            className="btn btn-outline-secondary me-2"
                            onClick={() => {
                              setStatusFilter("");
                              setStartDate("");
                              setEndDate("");
                              setStartTime("");
                              setEndTime("");
                              setSearchQuery("");
                            }}
                          >
                            Clear Filters
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* User Selection Table */}
                  <div className="mt-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">
                        {filteredUsers.length} Users match your criteria
                        {selectedUsers.length > 0 && (
                          <span className="text-primary ms-2">
                            ({selectedUsers.length} selected)
                          </span>
                        )}
                      </h6>

                      {filteredUsers.length > 0 && (
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="selectAllUsers"
                            checked={selectAll}
                            onChange={handleSelectAll}
                          />
                          <label
                            className="form-check-label small"
                            htmlFor="selectAllUsers"
                          >
                            Select all
                          </label>
                        </div>
                      )}
                    </div>

                    {/* User Table */}
                    <div
                      className="table-container"
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <table className="table table-hover table-sm">
                        <thead className="table-light sticky-top">
                          <tr>
                            <th width="40px">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={selectAll}
                                onChange={handleSelectAll}
                                disabled={filteredUsers.length === 0}
                              />
                            </th>
                            <th>Name</th>
                            <th>Contact</th>
                            <th>Email</th>
                            <th>Tags</th>
                            <th>Created</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredUsers.length > 0 ? (
                            filteredUsers.map((user, index) => (
                              <tr
                                key={index}
                                className={
                                  selectedUsers.includes(user.userId)
                                    ? "table-primary"
                                    : ""
                                }
                              >
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    value={user.userId}
                                    onChange={(e) =>
                                      handleUserSelection(e, user.userId)
                                    }
                                    checked={selectedUsers.includes(
                                      user.userId
                                    )}
                                  />
                                </td>
                                <td>
                                  <div className="d-flex align-items-center">
                                    <div
                                      className="bg-light rounded-circle d-flex align-items-center justify-content-center me-2"
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        fontSize: "14px",
                                        fontWeight: "500",
                                      }}
                                    >
                                      {user.userName
                                        ? user.userName.charAt(0).toUpperCase()
                                        : "U"}
                                    </div>
                                    <div>
                                      <div className="fw-medium">
                                        {user.userName || "Unknown"}
                                      </div>
                                      <small className="text-muted">
                                        ID: {user.userId}
                                      </small>
                                    </div>
                                  </div>
                                </td>
                                <td>{user.phoneNumber}</td>
                                <td>{user.emailId}</td>
                                <td>
                                  {user.tag && (
                                    <span className="badge bg-light text-dark border">
                                      {user.tag}
                                    </span>
                                  )}
                                </td>
                                <td>
                                  <div>
                                    <div>
                                      {new Date(
                                        user.createdAt
                                      ).toLocaleDateString()}
                                    </div>
                                    <small className="text-muted">
                                      {new Date(
                                        user.createdAt
                                      ).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      })}
                                    </small>
                                  </div>
                                </td>
                                <td>
                                  {user.statusCode === 1 ? (
                                    <span className="badge bg-success">
                                      Active
                                    </span>
                                  ) : (
                                    <span className="badge bg-secondary">
                                      Inactive
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center py-5">
                                <div className="d-flex flex-column align-items-center text-muted">
                                  <i className="bi bi-people-fill fs-1 mb-3"></i>
                                  <p className="mb-0">
                                    No users match your current filters
                                  </p>
                                  <small>
                                    Try adjusting your filters or search query
                                  </small>
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </TabPane>

              <TabPane tab="Use Segments" key="segments">
                <div className="pt-3">
                  <Alert
                    type="info"
                    message="Segment Selection"
                    description="Select segments to automatically include all users that match the segment rules. Duplicate users will be automatically removed."
                    className="mb-4"
                    showIcon
                  />

                  <SegmentSelector
                    selectedSegments={selectedSegments}
                    onSegmentChange={setSelectedSegments}
                    tenantEmail={tenantEmail}
                  />

                </div>
              </TabPane>
            </Tabs>

          </div>

          {/* Total Summary */}
          <div className="mt-3 p-3 bg-light rounded d-flex justify-content-between align-items-center">
            <div>
              <strong>Total recipients:</strong> {totalRecipients}
              {selectedSegments.length > 0 && (
                <span className="text-primary ms-2">
                  (Segments: {selectedSegments.length})
                </span>
              )}
            </div>
            <div className="text-end">
              {activeTab === "users" && (
                <button
                  className="btn btn-outline-secondary me-2"
                  onClick={toggleFilterPanel}
                >
                  {showFilters ? "Hide Filters" : "Show Filters"}
                </button>
              )}
              <span className="text-muted small">
                Manual: {manualContactsCount} | Users: {selectedUsers.length} |
                Segments: {segmentUsers.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudienceSection;
